import { useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddressList({ label, values, onChange, testId }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const commit = () => {
    const parts = draft.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const bad = parts.filter((p) => !EMAIL_RE.test(p));
    if (bad.length) { setError(`Not a valid email: ${bad.join(", ")}`); return; }
    onChange([...new Set([...values, ...parts])].slice(0, 20));
    setDraft(""); setError("");
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span><span className="text-[11px] text-slate-400">{values.length}/20</span></div>
      <div className="flex min-h-[28px] flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
        {values.map((v) => <span key={v} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-medium" data-testid={`${testId}-chip-${v}`}>{v}<button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`} data-testid={`${testId}-remove-${v}`}><X className="h-3 w-3 text-slate-500 hover:text-[#F92626]" /></button></span>)}
        {values.length === 0 && <span className="text-xs italic text-slate-400">None</span>}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => { setDraft(e.target.value); setError(""); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); } }} onBlur={commit} placeholder="name@example.com — Enter or comma to add" className="h-8 text-xs" data-testid={`${testId}-input`} />
        <Button type="button" size="sm" variant="outline" className="h-8" onClick={commit} disabled={values.length >= 20} data-testid={`${testId}-add`}>Add</Button>
      </div>
      {error && <div className="text-xs text-[#F92626]" data-testid={`${testId}-error`}>{error}</div>}
    </div>
  );
}

export function RecipientsEditor({ value, onChange, testId = "recipients" }) {
  const v = { to: [], cc: [], bcc: [], ...(value || {}) };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3" data-testid={testId}>
      <AddressList label="To" values={v.to} onChange={(to) => onChange({ ...v, to })} testId={`${testId}-to`} />
      <AddressList label="CC (delivered as additional visible recipients)" values={v.cc} onChange={(cc) => onChange({ ...v, cc })} testId={`${testId}-cc`} />
      <AddressList label="BCC (each address receives a separate hidden copy)" values={v.bcc} onChange={(bcc) => onChange({ ...v, bcc })} testId={`${testId}-bcc`} />
      <p className="text-[11px] text-slate-500">Any email address may be entered. Messages go through the managed Resend integration as "TQI Operations Hub".</p>
    </div>
  );
}

export const recipientCount = (r) => (r?.to?.length || 0) + (r?.cc?.length || 0) + (r?.bcc?.length || 0);
